import React, { useState } from 'react'
import { useAuth } from '../routes'
import { ROLE_MAP } from '../data'

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api'

type DevLoginFormProps = {
    onLoginSuccess?: () => void
}

export const DevLoginForm = ({ onLoginSuccess }: DevLoginFormProps) => {
    const { user } = useAuth()
    const [csid, setCsid] = useState('12345')
    const [role, setRole] = useState('admin')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [isMinimized, setIsMinimized] = useState(false)

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault()
        setLoading(true)
        setError(null)

        try {
            console.log('setting role: ', role);
            const response = await fetch(`${API_BASE_URL}/auth/dev/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    csid: parseInt(csid),
                    role,
                }),
            })

            if (!response.ok) {
                const data = await response.json()
                console.log('data', data);
                throw new Error(data.error || 'Login failed')
            }

            const data = await response.json()
            console.log('Dev login successful:', data)

            if (onLoginSuccess) {
                onLoginSuccess()
            } else {
                // Reload to update auth state
                window.location.reload()
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Login failed')
        } finally {
            setLoading(false)
        }
    }

    // Only show if dev login is enabled (local development or explicitly enabled)
    if (process.env.NODE_ENV !== 'development' && process.env.REACT_APP_ENABLE_DEV_LOGIN !== 'true') {
        return null
    }

    // Don't show if already logged in
    if (user) {
        return null
    }

    // Minimized state
    if (isMinimized) {
        return (
            <button
                onClick={() => setIsMinimized(false)}
                className="fixed bottom-4 right-4 bg-warn-600 hover:bg-warn-700 text-white font-medium py-2 px-4 rounded-lg shadow-lg z-50 transition-colors"
                title="Show Dev Login"
            >
                🛠️ Dev Login
            </button>
        )
    }

    return (
        <div className="fixed bottom-4 right-4 bg-warn-100 dark:bg-warn-900 border-2 border-warn-500 rounded-lg p-4 shadow-lg max-w-sm z-50">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <span className="text-2xl">🛠️</span>
                    <h3 className="font-bold text-warn-900 dark:text-warn-100">Dev Login</h3>
                </div>
                <button
                    onClick={() => setIsMinimized(true)}
                    className="text-warn-700 dark:text-warn-300 hover:text-warn-900 dark:hover:text-warn-100 text-xl leading-none"
                    title="Minimize"
                >
                    ×
                </button>
            </div>

            <form onSubmit={handleLogin} className="space-y-3">
                <div>
                    <label htmlFor="dev-csid" className="block text-sm font-medium text-warn-800 dark:text-warn-200 mb-1">
                        CSID
                    </label>
                    <input
                        id="dev-csid"
                        type="number"
                        value={csid}
                        onChange={(e) => setCsid(e.target.value)}
                        className="w-full px-3 py-2 border border-warn-300 dark:border-warn-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-warn-500"
                        placeholder="12345"
                        required
                    />
                </div>

                <div>
                    <label htmlFor="dev-role" className="block text-sm font-medium text-warn-800 dark:text-warn-200 mb-1">
                        Role
                    </label>
                    <select
                        id="dev-role"
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                        className="w-full px-3 py-2 border border-warn-300 dark:border-warn-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-warn-500"
                    >
                        {Object.entries(ROLE_MAP).map(([key, label]) => (
                            <option key={key} value={key}>{label}</option>
                        ))}
                    </select>
                </div>

                {error && (
                    <div className="text-sm text-danger-600 dark:text-danger-400 bg-danger-50 dark:bg-danger-900/20 px-3 py-2 rounded">
                        {error}
                    </div>
                )}

                <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-warn-600 hover:bg-warn-700 disabled:bg-warn-400 text-white font-medium py-2 px-4 rounded-md transition-colors"
                >
                    {loading ? 'Logging in...' : 'Quick Login'}
                </button>
            </form>

            <p className="mt-3 text-xs text-warn-700 dark:text-warn-300">
                Development only - bypasses OAuth
            </p>
        </div>
    )
}
